import { useCallback, useMemo } from 'react'
import { ResponsiveBar } from '@nivo/bar'
import { animated, useSprings, config } from '@react-spring/web'
import { useTooltip, BasicTooltip } from '@nivo/tooltip'
import type { BarCustomLayerProps } from '@nivo/bar'
import { Trans, useTranslation } from 'react-i18next'

import { EmissionsUnit, type ChartDataPoint } from 'types'
import { DataType } from 'types/data'

import './style.scss'

export enum BarChartGranularity {
  ANNUAL = 'Annual',
  MONTHLY = 'Monthly'
}

type Props = {
  data: ChartDataPoint[]
  labelX: string
  labelY: string
  granularity: BarChartGranularity
  baseline?: number
  dataType?: DataType
  unit?: EmissionsUnit
}

type Bar = BarCustomLayerProps<any>['bars'][number]

export default function BarChartNew({
  data,
  labelX,
  labelY,
  granularity,
  baseline,
  dataType,
  unit
}: Props) {
  const { i18n } = useTranslation()
  const isoLocaleCode = i18n.languages[0]
  const emissions: number[] = data.map((d: ChartDataPoint) => d.y)
  const min = Math.min(0, ...emissions)
  const max = Math.max(0, ...emissions)

  const formatDate = useCallback(
    (date: Date, hideMonth: boolean = false): string => {
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        ...(granularity === BarChartGranularity.MONTHLY && !hideMonth
          ? { month: 'long' }
          : null),
        timeZone: 'UTC'
      }
      return new Intl.DateTimeFormat(isoLocaleCode, options).format(date)
    },
    [i18n]
  )

  const chartData = useMemo(
    () =>
      data.map((d: ChartDataPoint) => ({
        x: formatDate(d.x as Date),
        y: d.y
      })),
    [data, i18n]
  )

  const yearTicks = useMemo(() => {
    if (granularity !== BarChartGranularity.MONTHLY) return undefined
    const seenYears = new Set<number>()
    const ticks: string[] = []
    data.forEach((d) => {
      const date = d.x as Date
      const year = date.getUTCFullYear()
      if (!seenYears.has(year)) {
        seenYears.add(year)
        ticks.push(formatDate(date))
      }
    })

    return ticks
  }, [data, granularity, formatDate])

  const getYear = (dateString: string) => {
    const utcDate = new Date(
      Date.UTC(
        new Date(dateString).getFullYear(),
        new Date(dateString).getMonth()
      )
    )

    return formatDate(utcDate, true)
  }

  const verticalMarkers =
    granularity === BarChartGranularity.MONTHLY && yearTicks != null
      ? yearTicks.map((tick) => ({
          axis: 'x' as const,
          value: tick,
          lineStyle: { stroke: '#ddd', strokeWidth: 1, strokeDasharray: '4 4' }
        }))
      : []

  const baselineMarker =
    baseline != null
      ? {
          axis: 'y' as const,
          value: baseline,
          lineStyle: {
            stroke: '#040444',
            strokeWidth: 2,
            strokeDasharray: '8 8'
          }
        }
      : null

  const markers = [
    ...verticalMarkers,
    ...(baselineMarker != null ? [baselineMarker] : [])
  ]

  const BarsWithCustomAnimation = (props: BarCustomLayerProps<any>) => {
    const { showTooltipFromEvent, hideTooltip } = useTooltip()
    const { bars, yScale } = props
    const barWidth = bars?.[0]?.width ?? 0
    const maxBarWidth = 40
    const finalWidth = Math.min(barWidth, maxBarWidth)

    const getFormattedValue = (value: number) => {
      let unitPrefix
      let numericValue

      if (unit != null) {
        unitPrefix = unit === EmissionsUnit.KTons ? 'kt' : 't'
        numericValue = value
      } else {
        const isMoreThanTonne = Math.abs(value) > 1000
        unitPrefix = isMoreThanTonne ? 'kt' : 't'
        numericValue = isMoreThanTonne ? value / 1000 : value
      }

      const formattedValue = numericValue.toFixed(2)

      return `${formattedValue} ${unitPrefix}CO₂`
    }

    const Tooltip = ({ bar }: { bar: Bar }) => (
      <BasicTooltip
        id={
          <div style={{ minWidth: 80 }}>
            <div>
              <Trans
                i18nKey="net_emissions_chart.tooltip_year"
                values={{ year: bar.data.data.x }}
              />
            </div>

            <div>
              {dataType === DataType.Intensity ? (
                <Trans
                  i18nKey="net_emissions_chart.tooltip_nee_intensity"
                  values={{ value: getFormattedValue(Number(bar.data.data.y)) }}
                />
              ) : (
                <Trans
                  i18nKey="net_emissions_chart.tooltip_nee"
                  values={{
                    value: getFormattedValue(Number(bar.data.data.y))
                  }}
                />
              )}
            </div>
          </div>
        }
      />
    )

    const springs = useSprings(
      bars.length,
      bars.map((bar) => {
        const zeroY = yScale(0)
        return {
          from: { y: zeroY, height: 0 },
          to: { y: bar.y, height: bar.height },
          config: config.gentle
        }
      })
    )

    return (
      <>
        {springs.map((spring, i) => {
          const bar = bars[i]
          return (
            <animated.rect
              key={bar.key}
              x={
                granularity === BarChartGranularity.ANNUAL
                  ? bar.x + (bar.width - finalWidth) / 2
                  : bar.x
              }
              width={
                granularity === BarChartGranularity.ANNUAL
                  ? finalWidth
                  : bar.width
              }
              fill={bar.color}
              y={spring.y}
              height={spring.height}
              onMouseEnter={(event) =>
                // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
                showTooltipFromEvent(<Tooltip bar={bar} />, event)
              }
              onMouseMove={(event) =>
                // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
                showTooltipFromEvent(<Tooltip bar={bar} />, event)
              }
              onMouseLeave={hideTooltip}
            />
          )
        })}
      </>
    )
  }

  return (
    <div className="cst-bar-chart-new-wrapper">
      <ResponsiveBar
        data={chartData}
        indexBy="x"
        labelSkipWidth={12}
        labelSkipHeight={12}
        colors={({ data }) => (Number(data?.y) < 0 ? '#007a3c' : '#b22222')}
        axisBottom={{
          legend: labelX,
          legendOffset: 52,
          ...(granularity === BarChartGranularity.MONTHLY
            ? {
                tickValues: yearTicks,
                tickSize: 0,
                format: (value) => getYear(value as string)
              }
            : {})
        }}
        markers={markers}
        axisLeft={{
          legend: labelY,
          legendOffset: -72
        }}
        margin={{ top: 72, right: 72, bottom: 72, left: 92 }}
        keys={['y']}
        valueScale={{ type: 'linear', min, max }}
        layout="vertical"
        animate={true}
        motionConfig="gentle"
        enableGridY={true}
        layers={['grid', 'axes', BarsWithCustomAnimation, 'markers']}
      />
    </div>
  )
}
